import Script from "next/script";
import React from "react";
import Footer from "../components/Footer";
import { useRouter } from "next/router";
import NavBar from "../components/NavBar";
import useUser from "../hooks/useUser";
import ScoutBar from "../components/ScoutBar";
import useSnackbar from "../hooks/useSnackbar";
import Speaker from "../Icons/Speaker";
import IconButton from "../components/Micro/IconButton";
import Link from "next/link";
import { BsArrowRight } from "react-icons/bs";
import useFetch from "@/hooks/useFetch";
import config from "@/config";
import WarningIcon from "@/Icons/WarningIcon";
import Loading from "@/components/Loading";
import { SUBSCRIPTION_PLAN_TYPE } from "@brimble/models";
import { format } from "date-fns";

const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, error } = useUser();

  const { close, setSnackbar } = useSnackbar();

  const router = useRouter();

  const { data: cards } = useFetch(config.PAYMENT_BASE_URL + "/cards", {
    keepPreviousData: true,
  });

  const isDisabled =
    user?.build_disabled && user?.build_disabled_by === "system";

  const [progressDuration, setProgressDuration] = React.useState(() => {
    const last =
      typeof window !== "undefined"
        ? localStorage.getItem("lastLoadDuration")
        : null;
    return last ? parseInt(last, 10) : 3000;
  });
  const loadStartRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (loading) {
      loadStartRef.current = Date.now();
    } else if (loadStartRef.current) {
      const elapsed = Date.now() - loadStartRef.current;
      setProgressDuration(elapsed);
      if (typeof window !== "undefined") {
        localStorage.setItem("lastLoadDuration", elapsed.toString());
      }
    }
  }, [loading]);

  React.useEffect(() => {
    const hasSeen = localStorage.getItem("snack-bar-upgrade-pro") == "true";

    const hasPendingDowngrade = user?.subscription?.is_pending_downgrade;

    const isFreePLan =
      user?.subscription?.plan_type == SUBSCRIPTION_PLAN_TYPE.FreePlan;

    if (!user) return;

    if (hasPendingDowngrade) {
      const readable = format(
        new Date(user.subscription.expiry_date as any),
        "MMMM d, yyyy",
      );
      setSnackbar({
        content: (
          <div className="flex justify-between">
            <div className="flex items-center space-x-1 rounded-full text-xs font-medium text-black md:text-base">
              <IconButton className="btn-sm border-0 bg-warning hover:bg-warning">
                <WarningIcon className="bg-warning" />
              </IconButton>
              <div className="flex items-center space-x-4 p-2">
                <p className="hidden text-sm font-medium md:block lg:text-base">
                  {" "}
                  Your plan will be downgraded to the free plan on {readable}.
                  You will lose access to paid features after this date.
                </p>
                <Link href={"/settings?tab=subscription"}>
                  <a className="flex items-center space-x-2">
                    <span className="font-bold text-primary">
                      {" "}
                      Manage Subscription{" "}
                    </span>
                    <IconButton light className="btn-sm bg-primary/10">
                      <span className="rounded-full bg-primary p-1 text-white">
                        <BsArrowRight size={10} />
                      </span>
                    </IconButton>
                  </a>
                </Link>
              </div>
            </div>
          </div>
        ),
        type: "warning",
        open: true,
        onClose: close,
        isCloseable: true,
      });
    } else if (isDisabled) {
      setSnackbar({
        content: (
          <div className="flex justify-between">
            <div className="flex items-center space-x-1 rounded-full text-xs font-medium text-black md:text-base">
              <IconButton className="btn-sm border-0 bg-red-600 hover:bg-red-600">
                <WarningIcon className="bg-red-600" />
              </IconButton>
              <div className="flex items-center space-x-4 p-2">
                <p className="hidden text-sm font-medium md:block lg:text-base">
                  Your account has been disabled. Contact support or upgrade to
                  a paid plan to continue using brimble.
                </p>
                <Link href={"/settings?tab=subscription"}>
                  <a className="flex items-center space-x-2">
                    <span className="font-bold text-primary">Upgrade Now</span>
                    <IconButton light className="btn-sm bg-primary/10">
                      <span className="rounded-full bg-primary p-1 text-white">
                        <BsArrowRight size={10} />
                      </span>
                    </IconButton>
                  </a>
                </Link>
              </div>
            </div>
          </div>
        ),
        type: "error",
        open: true,
        onClose: close,
        isCloseable: false,
      });
    }

    if (
      cards?.data?.length === 0 &&
      user?.build_disabled &&
      user?.build_disabled_by === "system"
    ) {
      setSnackbar({
        content: (
          <div className="flex justify-between">
            <div className="flex items-center space-x-1 rounded-full text-xs font-medium text-black md:text-base">
              <IconButton className="btn-sm border-0 bg-red-600 hover:bg-red-600">
                <WarningIcon className="bg-red-600" />
              </IconButton>
              <div className="p-2">
                <p className="hidden text-sm font-medium md:block lg:text-base">
                  Add a payment method to keep using brimble. Your builds are
                  temporarily disabled
                </p>
              </div>
            </div>
          </div>
        ),
        type: "error",
        open: true,
        onClose: close,
        isCloseable: false,
      });
    } else if (isFreePLan && !hasSeen) {
      setSnackbar({
        content: (
          <div className="flex justify-between">
            <div className="flex items-center space-x-1 rounded-full text-xs font-medium text-black md:text-base">
              <IconButton className="btn-sm">
                <Speaker />
              </IconButton>
              <p className="hidden text-sm font-medium md:block lg:text-base">
                Hi {user?.first_name}! Don&apos;t miss out, upgrade to Pro today
                and enjoy all the cool stuff 🚀
              </p>
              <p className="block font-medium md:hidden">
                Upgrade to Pro today and enjoy all the cool stuff 🚀
              </p>
              <Link href={"/settings?tab=subscription"}>
                <a className="flex items-center space-x-2">
                  <span className="font-bold text-primary">Upgrade Now</span>
                  <IconButton light className="btn-sm bg-primary/10">
                    <span className="rounded-full bg-primary p-1 text-white">
                      <BsArrowRight size={10} />
                    </span>
                  </IconButton>
                </a>
              </Link>
            </div>
          </div>
        ),
        type: "info",
        open: user?.subscription?.plan_type == SUBSCRIPTION_PLAN_TYPE.FreePlan,
        onClose: () => {
          localStorage.setItem("snack-bar-upgrade-pro", "true");
        },
      });
    }
  }, [user, router]);

  if (loading || error) {
    return <Loading loading={true} progressDuration={progressDuration} />;
  }

  return (
    <>
      <Script id="spotlight" strategy="afterInteractive">{`
        document.addEventListener("keydown", function(event) {
          if (event.ctrlKey && event.key === "k") {
            event.preventDefault();
          }
        })
      `}</Script>

      <ScoutBar />
      <NavBar user={user} />
      <main className="container mx-auto flex min-h-screen max-w-7xl flex-col px-4 pb-20">
        {children}
      </main>
      <Footer />
    </>
  );
};

export default DashboardLayout;
